import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Merge, Monitor, QrCode, RefreshCw, UserPlus } from "lucide-react";
import { api, minutesSince } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { reasonsFor, reasonsForFloor, labelCase, isFloorBranch, DEPARTMENTS, deptLabel, isQuick } from "@/lib/queueConfig";

const STATUS_CLS = {
  waiting: "bg-[#0A0A0A] text-white",
  serving: "bg-[#C5A059] text-[#0A0A0A]",
  done: "bg-muted text-foreground",
  no_show: "bg-[#9A3412] text-white",
  merged: "bg-muted text-muted-foreground",
};

const waited = (iso) => {
  if (!iso) return "";
  const mins = minutesSince(iso);
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export default function Queue() {
  const { branch } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [flags, setFlags] = useState({});
  const [showDone, setShowDone] = useState(false);
  const [picked, setPicked] = useState([]);
  const [adding, setAdding] = useState(false);
  const [manual, setManual] = useState({
    name: "", phone: "", reason: "ENQUIRY", services: [], department: "", note: "", party_size: 1,
  });
  const [floorFilter, setFloorFilter] = useState("");

  const [qr, setQr] = useState(null);

  const showQr = async () => {
    try {
      const res = await api.get("/counter-qr", { params: { branch }, responseType: "blob" });
      setQr({ [branch]: URL.createObjectURL(res.data) });
    } catch (err) {
      toast.error("Could not build the QR code");
    }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const load = useCallback(() => {
    api
      .get("/queue/stats", { params: { branch } })
      .then(({ data }) => setStats(data))
      .catch(() => {});
    api
      .get("/queue", { params: { branch, include_done: showDone } })
      .then(async ({ data }) => {
        setRows(data);
        const picked = data.slice(0, 12);
        const results = await Promise.all(
          picked.map((r) =>
            api
              .get("/cash-watch/status", { params: { phone: r.phone } })
              .then(({ data }) => [r.id, data])
              .catch(() => [r.id, null])
          )
        );
        setFlags(Object.fromEntries(results));
      })
      .catch(() => toast.error("Could not load the queue"));
  }, [branch, showDone]);

  // A ticket can hold several services; older tickets saved before this feature only
  // carry a single reason, so fall back to that so the row always renders.
  const serviceList = (r) => {
    const svcs = r.services || [];
    if (svcs.length) return svcs;
    return [{ reason: r.reason, done: r.status === "done", done_by: r.served_by }];
  };

  const toggleService = async (r, idx, done) => {
    try {
      await api.post(`/queue/${r.id}/service`, { index: idx, done });
      load();
    } catch (err) {
      toast.error("Could not update that service");
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load]);

  // Keep the walk-in default service valid for the current branch — Truganina's list
  // has no plain "ENQUIRY", so the stored reason must not fall out of step with the
  // dropdown shown on screen.
  const defaultReason = isFloorBranch(branch) ? "GENERAL ENQUIRY" : "ENQUIRY";
  useEffect(() => {
    setManual((m) => ({ ...m, reason: defaultReason }));
  }, [defaultReason]);

  const setStatus = async (id, status, outcome) => {
    try {
      await api.post(`/queue/${id}/status`, outcome ? { status, outcome } : { status });
      load();
    } catch (err) {
      toast.error("Could not update the queue");
    }
  };

  const merge = async () => {
    const keep = rows.filter((r) => picked.includes(r.id)).sort((a, b) => a.ticket - b.ticket)[0];
    if (!keep) return;
    try {
      const { data } = await api.post("/queue/merge", {
        keep_id: keep.id,
        merge_ids: picked.filter((id) => id !== keep.id),
      });
      toast.success(`Merged into ticket #${data.ticket_label || data.ticket} — party of ${data.party_size}`);
      setPicked([]);
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not merge those tickets"
      );
    }
  };

  const addManual = async () => {
    if (!manual.name.trim()) return toast.error("Give the customer's name");
    if (showFloor && !manual.department)
      return toast.error("Choose a floor (Gold or Silver) for this walk-in");
    try {
      const { data } = await api.post("/queue", { ...manual, branch });
      toast.success(`Ticket #${data.ticket_label || data.ticket} added`);
      setManual({ name: "", phone: "", reason: defaultReason, services: [], department: "", note: "", party_size: 1 });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not add them"
      );
    }
  };

  const showFloor = isFloorBranch(branch);
  const reasons = reasonsFor(branch);
  // Only floor branches filter by floor — a filter left over from Truganina must not
  // hide another store's queue after a branch switch.
  const isTruganina = (branch || "").toUpperCase() === "TRUGANINA";
  const visibleRows = showFloor && floorFilter
    ? rows.filter((r) => (r.department || "").toUpperCase() === floorFilter)
    : rows;
  const nextWaiting = visibleRows.find((r) => r.status === "waiting");
  const longestWait = visibleRows
    .filter((r) => r.status === "waiting")
    .reduce((m, r) => Math.max(m, minutesSince(r.joined_at)), 0);
  // When a floor filter is on, the "Waiting now" count follows the visible list so the
  // number on screen always matches the rows underneath it.
  const waitingNow =
    showFloor && floorFilter
      ? visibleRows.filter((r) => r.status === "waiting").length
      : stats?.waiting;
  const servingNow =
    showFloor && floorFilter
      ? visibleRows.filter((r) => r.status === "serving").length
      : stats?.serving;

  return (
    <div className="fade-up" data-testid="queue-board">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Queue</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Customers who scanned the counter QR code today — one ticket per group.
            Refreshes on its own.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={showQr}
            data-testid="counter-qr-button"
          >
            <QrCode className="mr-1.5 h-4 w-4" /> Counter QR code
          </Button>
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="queue-refresh">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => window.open(`/queue-display/${branch}`, "_blank")}
            data-testid="queue-display-button"
          >
            <Monitor className="mr-1.5 h-4 w-4" /> Customer screen
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => setAdding((a) => !a)}
            data-testid="queue-add-toggle"
          >
            <UserPlus className="mr-1.5 h-4 w-4" /> Add walk-in
          </Button>
          {nextWaiting && (
            <Button
              className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              onClick={() => setStatus(nextWaiting.id, "serving")}
              data-testid="queue-call-next"
            >
              Call next (#{nextWaiting.ticket_label || nextWaiting.ticket})
            </Button>
          )}
        </div>
      </div>

      {stats && (
        <div
          className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5"
          data-testid="queue-stats-strip"
        >
          <StatCard label="Waiting now" value={waitingNow} testid="queue-stat-waiting" tone="dark" />
          <StatCard
            label="Longest wait"
            value={longestWait ? `${longestWait} min` : "—"}
            testid="queue-stat-longest"
            tone={longestWait >= 15 ? "warn" : ""}
          />
          <StatCard label="Now serving" value={servingNow} testid="queue-stat-serving" />
          <StatCard
            label="Served today"
            value={stats.served_today}
            testid="queue-stat-served"
            tone="good"
          />
          <StatCard
            label="No-shows today"
            value={stats.no_show_today}
            testid="queue-stat-noshow"
            tone={stats.no_show_today > 0 ? "warn" : ""}
          />
        </div>
      )}

      {isTruganina && stats && (
        <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground"
           data-testid="queue-outcome-tally">
          <span className="text-emerald-700">{stats.bought_today || 0} bought</span>
          {" · "}
          <span>{stats.no_sale_today || 0} no sale</span>
        </p>
      )}

      {qr && (
        <div className="mt-5 flex flex-col items-center" data-testid="counter-qr-panel">
          <div className="print-area w-full max-w-[520px]" data-testid={`counter-qr-card-${branch}`}>
            <div
              className="relative overflow-hidden px-10 py-10 text-center"
              style={{ backgroundColor: "#F8F5EC", border: "2px solid #C4A87C" }}
            >
              <div
                className="pointer-events-none absolute inset-[10px]"
                style={{ border: "1px solid #D8C49A" }}
              />
              <div className="relative">
                <div className="flex items-center justify-center gap-3">
                  <img
                    src="/brand/logo.png"
                    alt="Dhunna Jewellers"
                    className="h-12 w-auto object-contain"
                    style={{ mixBlendMode: "multiply" }}
                  />
                  <span
                    className="font-invoice text-xl uppercase tracking-[0.2em]"
                    style={{ color: "#4A3C2A" }}
                  >
                    Dhunna Jewellers
                  </span>
                </div>

                <div className="mx-auto mt-5 h-px w-24" style={{ backgroundColor: "#C4A87C" }} />

                <h2
                  className="mt-5 text-2xl font-bold uppercase tracking-[0.15em]"
                  style={{ color: "#4A3C2A" }}
                >
                  Join the Queue
                </h2>
                <p className="mt-2 text-sm italic" style={{ color: "#6B5B41" }}>
                  Scan on arrival — we'll call you by number
                </p>

                <div
                  className="mx-auto mt-6 inline-block bg-white p-4"
                  style={{ border: "1px solid #E0D6BE" }}
                >
                  <img
                    src={qr[branch]}
                    alt={`QR code for ${branch}`}
                    className="h-56 w-56"
                    data-testid={`counter-qr-image-${branch}`}
                  />
                </div>

                <p
                  className="mt-5 text-sm font-semibold uppercase tracking-[0.25em]"
                  style={{ color: "#4A3C2A" }}
                >
                  Scan to join
                </p>

                <div
                  className="mx-auto mt-4 inline-block px-5 py-1.5 text-xs font-semibold uppercase tracking-[0.3em]"
                  style={{ backgroundColor: "#EDE6D7", color: "#5E4A1C", border: "1px solid #D8C49A" }}
                  data-testid={`counter-qr-branch-${branch}`}
                >
                  {branch} Store
                </div>

                <p className="mt-6 text-xs" style={{ color: "#6B5B41" }}>
                  Please ask a staff member for assistance
                </p>
              </div>
            </div>
          </div>

          <p className="no-print mt-3 text-xs text-muted-foreground">
            Switch stores at the top of the screen to print the other one.
          </p>
          <div className="no-print mt-4 flex justify-center gap-2">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => window.print()}
              data-testid="counter-qr-print"
            >
              Print this card
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => setQr(null)}
              data-testid="counter-qr-close"
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {adding && (
        <div className="mt-4 border border-border bg-[#FBF8F2] p-4" data-testid="queue-add-panel">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Add a walk-in by hand — always gets its own ticket, even for a phone already in
            the queue (mother buying, son selling).
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Input
              className="h-10 w-[190px] rounded-none"
              placeholder="Customer name"
              value={manual.name}
              onChange={(e) => setManual({ ...manual, name: e.target.value })}
              data-testid="queue-add-name"
            />
            <Input
              className="h-10 w-[160px] rounded-none"
              placeholder="Phone"
              value={manual.phone}
              onChange={(e) => setManual({ ...manual, phone: e.target.value })}
              data-testid="queue-add-phone"
            />
            {showFloor && (
              <select
                className="h-10 border border-input bg-white px-2 text-sm"
                value={manual.department}
                onChange={(e) =>
                  setManual((m) => ({
                    ...m,
                    department: e.target.value,
                    services: m.services.filter((s) =>
                      reasonsForFloor(branch, e.target.value).includes(s)
                    ),
                  }))
                }
                data-testid="queue-add-department"
              >
                <option value="">Floor…</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label} · {d.floor}
                  </option>
                ))}
              </select>
            )}
            {showFloor ? (
              manual.department ? (
              <div className="w-full" data-testid="queue-add-services">
                <p className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                  Here for (tick all that apply)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {reasonsForFloor(branch, manual.department).map((r) => {
                    const on = manual.services.includes(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() =>
                          setManual((m) => ({
                            ...m,
                            services: on
                              ? m.services.filter((x) => x !== r)
                              : [...m.services, r],
                          }))
                        }
                        className={`border px-2.5 py-1.5 text-xs transition-colors ${
                          on
                            ? "border-[#C5A059] bg-[#C5A059] font-semibold text-[#0A0A0A]"
                            : "border-input bg-white hover:border-[#C5A059]/60"
                        }`}
                        data-testid={`queue-add-service-${r.replace(/[^A-Za-z0-9]+/g, "-")}`}
                      >
                        {labelCase(r)}
                      </button>
                    );
                  })}
                </div>
              </div>
              ) : null
            ) : (
              <select
                className="h-10 border border-input bg-white px-2 text-sm"
                value={manual.services[0] || manual.reason || "ENQUIRY"}
                onChange={(e) =>
                  setManual((m) => ({ ...m, reason: e.target.value, services: [e.target.value] }))
                }
                data-testid="queue-add-reason"
              >
                {reasons.map((r) => (
                  <option key={r} value={r}>
                    {labelCase(r)}
                  </option>
                ))}
              </select>
            )}
            <Input
              className="h-10 w-[200px] rounded-none"
              placeholder="Note (optional)"
              value={manual.note}
              onChange={(e) => setManual({ ...manual, note: e.target.value })}
              data-testid="queue-add-note"
            />
            <Input
              type="number"
              min="1"
              max="20"
              className="h-10 w-[110px] rounded-none"
              value={manual.party_size}
              onChange={(e) => setManual({ ...manual, party_size: Number(e.target.value) || 1 })}
              data-testid="queue-add-party"
            />
            <Button
              className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
              onClick={addManual}
              data-testid="queue-add-save"
            >
              Add to queue
            </Button>
          </div>
        </div>
      )}

      {picked.length > 1 && (
        <div
          className="mt-4 flex flex-wrap items-center gap-3 border border-[#C5A059] bg-[#FBF8F2] p-3"
          data-testid="queue-merge-bar"
        >
          <span className="text-sm">
            {picked.length} tickets selected — merge them into the earliest one as a single
            party?
          </span>
          <Button
            className="h-9 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            onClick={merge}
            data-testid="queue-merge-button"
          >
            <Merge className="mr-1.5 h-4 w-4" /> Merge into one ticket
          </Button>
          <button
            type="button"
            onClick={() => setPicked([])}
            className="text-xs uppercase tracking-wider text-muted-foreground underline"
            data-testid="queue-merge-clear"
          >
            Clear
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-4">
        {showFloor && (
          <div className="flex items-center gap-2" data-testid="queue-floor-filter">
            {[{ key: "", label: "All floors" }, ...DEPARTMENTS].map((d) => (
              <button
                key={d.key || "all"}
                type="button"
                onClick={() => setFloorFilter(d.key)}
                className={`border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  floorFilter === d.key
                    ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0A]"
                    : "border-border text-muted-foreground"
                }`}
                data-testid={`queue-floor-${d.key || "all"}`}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            data-testid="queue-show-done"
          />
          Include finished
        </label>
      </div>

      <div className="mt-5 overflow-x-auto border border-border">
        <table className="w-full min-w-[860px] text-sm" data-testid="queue-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              <th className="w-10 px-2 py-2.5" />
              {["No.", "Customer", "Here for", "Waiting", "Flags", "Status", ""].map((h, i) => (
                <th key={i} className="px-3 py-2.5 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-12 text-center text-muted-foreground">
                  Nobody in the queue.
                </td>
              </tr>
            )}
            {visibleRows.map((r) => {
              const f = flags[r.id];
              const urgent = r.status === "waiting" && minutesSince(r.joined_at) >= 15;
              return (
                <tr
                  key={r.id}
                  className={`hover:bg-[#FBF8F2] ${urgent ? "bg-[#FEF4E6]" : ""}`}
                  style={urgent ? { boxShadow: "inset 3px 0 0 #C2630F" } : undefined}
                  data-testid={`queue-row-${r.ticket}`}
                >
                  <td className="px-2">
                    {["waiting", "serving"].includes(r.status) && (
                      <input
                        type="checkbox"
                        checked={picked.includes(r.id)}
                        onChange={() => togglePick(r.id)}
                        data-testid={`queue-pick-${r.ticket}`}
                      />
                    )}
                  </td>
                  <td className="tabular px-3 py-2.5 text-lg font-bold">{r.ticket_label || r.ticket}</td>
                  <td className="px-3 py-2.5">
                    {r.phone ? (
                      <button
                        type="button"
                        onClick={() => window.open(`/customers/${encodeURIComponent(r.phone)}`, "_blank", "noopener")}
                        className="text-left font-medium text-[#8A6C36] underline-offset-2 hover:underline"
                        title="Open customer record"
                        data-testid={`queue-customer-link-${r.ticket}`}
                      >
                        {r.name}
                      </button>
                    ) : (
                      r.name
                    )}
                    {(r.party_size || 1) > 1 && (
                      <span
                        className="ml-2 border border-[#C5A059] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A6C36]"
                        data-testid={`queue-party-${r.ticket}`}
                      >
                        Party of {r.party_size}
                      </span>
                    )}
                    <div className="tabular text-xs text-muted-foreground">{r.phone}</div>
                    {r.note && (
                      <div className="break-words text-xs text-muted-foreground">{r.note}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    <div className="space-y-1">
                      {serviceList(r).map((s, i) => (
                        <label
                          key={i}
                          className="flex items-center gap-1.5"
                          data-testid={`queue-service-${r.ticket}-${i}`}
                        >
                          {serviceList(r).length > 1 && (["waiting", "serving"].includes(r.status) || s.done) && (
                            <input
                              type="checkbox"
                              checked={!!s.done}
                              onChange={(e) => toggleService(r, i, e.target.checked)}
                              className="h-3.5 w-3.5"
                              data-testid={`queue-service-check-${r.ticket}-${i}`}
                            />
                          )}
                          <span
                            className={`uppercase tracking-wider ${
                              s.done ? "text-muted-foreground line-through" : ""
                            }`}
                          >
                            {labelCase(s.reason)}
                          </span>
                          {isQuick(s.reason) && !s.done && (
                            <span
                              className="bg-[#166534] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white"
                              data-testid={`queue-quick-${r.ticket}-${i}`}
                            >
                              Quick
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                    {serviceList(r).length > 1 && (
                      <div
                        className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#8A6C36]"
                        data-testid={`queue-service-progress-${r.ticket}`}
                      >
                        {serviceList(r).filter((s) => s.done).length} of{" "}
                        {serviceList(r).length} done
                      </div>
                    )}
                    {r.department && (
                      <div
                        className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#8A6C36]"
                        data-testid={`queue-rowdept-${r.ticket}`}
                      >
                        {deptLabel(r.department)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">
                    {waited(r.joined_at)}
                  </td>
                  <td className="px-3 py-2.5">
                    {f?.flagged && (
                      <span className="mr-1 bg-[#9A3412] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                            data-testid={`queue-flag-cash-${r.ticket}`}>
                        Cash 10k
                      </span>
                    )}
                    {f?.warning && (
                      <span className="mr-1 bg-[#C5A059] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#0A0A0A]">
                        Near 10k
                      </span>
                    )}
                    {f && !f.has_kyc && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        no KYC
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-1 text-[10px] font-bold uppercase ${STATUS_CLS[r.status]}`}
                          data-testid={`queue-status-${r.ticket}`}>
                      {r.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {r.status === "waiting" && (
                      <button type="button" onClick={() => setStatus(r.id, "serving")}
                              className="mr-3 text-xs uppercase tracking-wider text-[#B38F4E]"
                              data-testid={`queue-serve-${r.ticket}`}>
                        Serve
                      </button>
                    )}
                    {r.status === "no_show" && (
                      <button type="button" onClick={() => setStatus(r.id, "waiting")}
                              className="mr-3 text-xs font-semibold uppercase tracking-wider text-[#B38F4E]"
                              data-testid={`queue-bringback-${r.ticket}`}>
                        Bring back
                      </button>
                    )}
                    {r.status !== "done" && (isTruganina ? (
                      <>
                        <button type="button" onClick={() => setStatus(r.id, "done", "bought")}
                                className="mr-3 text-xs font-semibold uppercase tracking-wider text-emerald-700"
                                data-testid={`queue-bought-${r.ticket}`}>
                          Bought
                        </button>
                        <button type="button" onClick={() => setStatus(r.id, "done", "no_sale")}
                                className="mr-3 text-xs uppercase tracking-wider text-muted-foreground"
                                data-testid={`queue-nosale-${r.ticket}`}>
                          No sale
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setStatus(r.id, "done")}
                              className="mr-3 text-xs uppercase tracking-wider text-muted-foreground"
                              data-testid={`queue-done-${r.ticket}`}>
                        Done
                      </button>
                    ))}
                    {r.status === "waiting" && (
                      <button type="button" onClick={() => setStatus(r.id, "no_show")}
                              className="text-xs uppercase tracking-wider text-muted-foreground"
                              data-testid={`queue-noshow-${r.ticket}`}>
                        No show
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, testid, tone }) => (
  <div
    className={`border p-4 ${
      tone === "dark"
        ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
        : tone === "warn"
        ? "border-[#9A3412] bg-[#FDF7F4]"
        : tone === "good"
        ? "border-[#166534]/40 bg-[#F0FDF4]"
        : "border-border"
    }`}
    data-testid={testid}
  >
    <div
      className={`text-[11px] font-semibold uppercase tracking-wider ${
        tone === "dark" ? "text-white/70" : "text-muted-foreground"
      }`}
    >
      {label}
    </div>
    <div className="tabular mt-1 text-3xl font-bold">{value}</div>
  </div>
);
